import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Flame, EnvelopeSimple, ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STEPS = { EMAIL: 'email', OTP: 'otp', DONE: 'done' };

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const otpRefs = useRef([]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email enter karo'); return; }
    setLoading(true);
    setError('');
    try {
      await forgotPassword(email.trim());
      toast.success('OTP bhej diya gaya hai aapke email pe!');
      setStep(STEPS.OTP);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Request failed. Try again.');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) { setError('Poora 6-digit OTP enter karo'); return; }
    if (!newPassword) { setError('New password enter karo'); return; }
    if (newPassword.length < 6) { setError('Password kam se kam 6 characters ka hona chahiye'); return; }
    if (newPassword !== confirmPassword) { setError('Dono passwords match nahi kar rahe'); return; }
    setLoading(true);
    setError('');
    try {
      await resetPassword({ email: email.trim(), otp: otpString, new_password: newPassword });
      toast.success('Password change ho gaya!');
      setStep(STEPS.DONE);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Reset failed. Try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-gray-200 p-8">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="bg-[#0C1D3F] px-4 py-2 rounded">
            <span className="text-sm font-bold text-white tracking-wide">AXIALUP</span>
          </div>
        </div>

        {/* Step: Enter Email */}
        {step === STEPS.EMAIL && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Forgot Password?</h2>
            <p className="text-sm text-gray-500 mb-5">Apna registered email enter karo, hum OTP bhejenge</p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-error">{error}</div>}
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <Label className="form-label">Email</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 h-11"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-11" disabled={loading} data-testid="forgot-send-otp-btn">
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid="back-to-login-link">
                <ArrowLeft size={14} /> Back to Login
              </Link>
            </div>
          </>
        )}

        {/* Step: Enter OTP + New Password */}
        {step === STEPS.OTP && (
          <>
            <h2 className="text-lg font-bold text-gray-900 mb-1">OTP Verify Karo</h2>
            <p className="text-sm text-gray-500 mb-1">
              <strong>{email}</strong> pe OTP bheja gaya hai
            </p>
            <p className="text-xs text-amber-600 mb-5">OTP 10 minute mein expire hoga. Max 3 attempts.</p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="otp-error">{error}</div>}
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* OTP Input Boxes */}
              <div>
                <Label className="form-label">6-Digit OTP</Label>
                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={`otp-${i}`}
                      ref={(el) => otpRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-11 h-12 text-center text-xl font-bold border border-gray-300 rounded-md focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none"
                      data-testid={`otp-input-${i}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label className="form-label">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="h-11"
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label className="form-label">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Same password again"
                  className="h-11"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-11" disabled={loading} data-testid="reset-password-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
            <div className="mt-3 flex justify-between text-xs">
              <button onClick={() => { setStep(STEPS.EMAIL); setError(''); setOtp(['', '', '', '', '', '']); }} className="text-gray-500 hover:text-gray-700" data-testid="change-email-btn">
                Change Email
              </button>
              <button onClick={handleSendOtp} disabled={loading} className="text-[#002FA7] hover:underline" data-testid="resend-otp-btn">
                Resend OTP
              </button>
            </div>
          </>
        )}

        {/* Step: Success */}
        {step === STEPS.DONE && (
          <div className="text-center py-4" data-testid="reset-success">
            <ShieldCheck size={56} weight="fill" className="text-green-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-gray-900 mb-2">Password Change Ho Gaya!</h2>
            <p className="text-sm text-gray-500 mb-5">Ab apne naye password se login karo</p>
            <Link to="/login">
              <Button className="w-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-11" data-testid="go-to-login-btn">
                Login Page Pe Jao
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
